import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { addOverlays, setOverlayData, STYLES } from "./mapStyles";
import { createAmenityEl, createBaseEl, createOfficeEl, createPriceEl } from "./markers";
import { lineFeature, milesBetween, ringsCollection } from "./geo";
import { MapControls } from "./MapControls";

export const DEFAULT_VIEW = { center: [-76.2, 36.9], zoom: 9.4, pitch: 50, bearing: -12 };

function useMarkerLayer(mapRef, items, getKey, makeEl, ready) {
  const markers = useRef(new Map());
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const current = markers.current;
    const keys = new Set(items.map(getKey));
    current.forEach((m, k) => {
      if (!keys.has(k)) {
        m.marker.remove();
        current.delete(k);
      }
    });
    items.forEach((item) => {
      const k = getKey(item);
      if (current.has(k)) return;
      const el = makeEl(item);
      const marker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([item.lng, item.lat]).addTo(map);
      current.set(k, { marker, el });
    });
  }, [items, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  return markers;
}

/**
 * MapLibre 3D map: satellite / architectural 3D model / clean street map, price pins, base pins,
 * radius ring, home-to-base link line, cinematic fly-to and orbit.
 */
export const HomeMap = ({
  listings = [],
  bases = [],
  selectedBaseId = null,
  radius = null,
  activeKey = null,
  hoverKey = null,
  onListingClick,
  onBaseClick,
  focus = null,
  orbit = false,
  onOrbitChange,
  initialView = DEFAULT_VIEW,
  initialMode = "satellite",
  onBoundsChange,
  office = null,
  linkTarget = null,
  showBaseLabels = true,
  interactive = true,
  controls = true,
  controlsClass = "top-3",
  vignette = false,
  pois = [],
  parcelFeatures = null,
  sunlight = null,
  walkCommand = null,
  testId = "home-map",
  className = "",
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const overlay = useRef({ radius: null, link: null, parcels: null });
  const handlers = useRef({});
  handlers.current = { onListingClick, onBaseClick, onBoundsChange, onOrbitChange };
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState(initialMode);
  const [tilted, setTilted] = useState((initialView.pitch || 0) > 0);

  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLES[initialMode],
      ...initialView,
      maxPitch: 78,
      attributionControl: { compact: true },
      interactive,
      fadeDuration: 200,
    });
    if (interactive) map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "bottom-right");
    map.on("style.load", () => {
      addOverlays(map);
      setOverlayData(map, overlay.current);
      setReady(true);
    });
    map.on("moveend", () => {
      const b = map.getBounds();
      handlers.current.onBoundsChange?.({ west: b.getWest(), east: b.getEast(), south: b.getSouth(), north: b.getNorth() });
    });
    const stopOrbit = () => handlers.current.onOrbitChange?.(false);
    map.on("dragstart", stopOrbit);
    map.on("rotatestart", stopOrbit);
    map.on("pitchstart", stopOrbit);
    map.getCanvasContainer().addEventListener("wheel", stopOrbit, { passive: true });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const handlers = [map.scrollZoom, map.boxZoom, map.dragRotate, map.dragPan, map.keyboard, map.doubleClickZoom, map.touchZoomRotate];
    handlers.forEach((handler) => (interactive ? handler.enable() : handler.disable()));
  }, [interactive]);

  const listingMarkers = useMarkerLayer(mapRef, listings, (l) => l.listing_key,
    (l) => createPriceEl(l, () => handlers.current.onListingClick?.(l.listing_key)), ready);
  const baseMarkers = useMarkerLayer(mapRef, bases, (b) => b.id,
    (b) => createBaseEl(b, () => handlers.current.onBaseClick?.(b.id), showBaseLabels), ready);
  useMarkerLayer(mapRef, office ? [{ id: "office", ...office }] : [], (o) => o.id, () => createOfficeEl(), ready);
  useMarkerLayer(mapRef, pois, (p) => p.id, (p) => createAmenityEl(p), ready);

  useEffect(() => {
    listingMarkers.current.forEach(({ el }, k) => {
      const on = k === activeKey || k === hoverKey;
      el.classList.toggle("is-active", on);
      el.style.zIndex = on ? "30" : "1";
    });
  }, [activeKey, hoverKey, listings, ready, listingMarkers]);

  useEffect(() => {
    baseMarkers.current.forEach(({ el }, k) => {
      el.classList.toggle("is-selected", k === selectedBaseId);
      el.style.zIndex = k === selectedBaseId ? "25" : "5";
    });
  }, [selectedBaseId, bases, ready, baseMarkers]);

  useEffect(() => {
    const base = bases.find((b) => b.id === selectedBaseId);
    const home = listings.find((l) => l.listing_key === (hoverKey || activeKey));
    let link = null;
    if (linkTarget) link = lineFeature(linkTarget.from, linkTarget.to, linkTarget.label);
    else if (base && home) link = lineFeature(home, base, `${milesBetween(home, base).toFixed(1)} mi to ${base.short}`);
    overlay.current = { radius: base && radius ? ringsCollection([base.lng, base.lat], radius) : null, link, parcels: parcelFeatures };
    if (mapRef.current) setOverlayData(mapRef.current, overlay.current);
  }, [selectedBaseId, radius, hoverKey, activeKey, listings, bases, linkTarget, parcelFeatures, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !sunlight) return;
    const presets = {
      morning: { color: "#ffe0ae", intensity: 0.42, position: [1.2, 85, 45], brightness: 0.86 },
      midday: { color: "#ffffff", intensity: 0.3, position: [1.5, 180, 75], brightness: 0.98 },
      evening: { color: "#ffc18a", intensity: 0.28, position: [1.2, 275, 25], brightness: 0.76 },
    };
    const p = presets[sunlight];
    if (!p) return;
    try {
      map.setLight({ anchor: "map", color: p.color, intensity: p.intensity, position: p.position });
      if (map.getLayer("satellite")) map.setPaintProperty("satellite", "raster-brightness-max", p.brightness);
    } catch { /* Map style can change while a light preset is selected. */ }
  }, [sunlight, mode, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !walkCommand) return;
    const bearing = map.getBearing();
    if (walkCommand.action === "left" || walkCommand.action === "right") {
      map.easeTo({ bearing: bearing + (walkCommand.action === "left" ? -28 : 28), duration: 480 });
      return;
    }
    const direction = walkCommand.action === "back" ? -1 : 1;
    const radians = (bearing * Math.PI) / 180;
    const point = map.getCenter();
    const step = 0.00032 * direction;
    map.easeTo({ center: [point.lng + Math.sin(radians) * step, point.lat + Math.cos(radians) * step], zoom: Math.max(map.getZoom(), 16.2), pitch: 72, duration: 720 });
  }, [walkCommand]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    if (focus.bounds) {
      map.fitBounds(focus.bounds, {
        padding: focus.padding ?? 70, maxZoom: focus.maxZoom ?? 12, duration: focus.duration ?? 1600,
        pitch: focus.pitch ?? map.getPitch(), bearing: focus.bearing ?? map.getBearing(),
      });
      return;
    }
    map.flyTo({
      center: [focus.lng, focus.lat], zoom: focus.zoom ?? 13.8, pitch: focus.pitch ?? 62, curve: focus.curve ?? 1.42,
      bearing: focus.bearing ?? map.getBearing(), duration: focus.duration ?? 2800, essential: true,
      padding: focus.padding ?? { top: 0, bottom: 0, left: 0, right: 0 },
    });
    setTilted((focus.pitch ?? 62) > 0);
  }, [focus]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !orbit || !ready) return undefined;
    let raf;
    let last = performance.now();
    const step = (t) => {
      const dt = Math.min(t - last, 64);
      last = t;
      if (!map.isMoving()) map.setBearing((map.getBearing() + dt * 0.007) % 360);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [orbit, ready]);

  const switchMode = (m) => {
    setMode(m);
    mapRef.current?.setStyle(STYLES[m], { diff: false });
  };
  const toggleTilt = () => {
    mapRef.current?.easeTo({ pitch: tilted ? 0 : 60, duration: 900 });
    setTilted(!tilted);
  };
  const reset = () => {
    handlers.current.onOrbitChange?.(false);
    mapRef.current?.flyTo({ ...initialView, duration: 1800 });
    setTilted((initialView.pitch || 0) > 0);
  };

  return (
    <div className={`relative isolate h-full w-full overflow-hidden bg-neutral-900 ${className}`} data-testid={testId} data-map-mode={mode} data-map-ready={ready}>
      <div ref={containerRef} className={`absolute inset-0 transition-opacity duration-1000 ${ready ? "opacity-100" : "opacity-0"}`} />
      {vignette && (
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_160px_40px_rgba(0,0,0,0.45)]" />
      )}
      {controls && (
        <MapControls
          className={controlsClass}
          mode={mode}
          onMode={switchMode}
          tilted={tilted}
          onTilt={toggleTilt}
          orbit={orbit}
          canOrbit={Boolean(onOrbitChange)}
          onOrbit={() => onOrbitChange?.(!orbit)}
          onReset={reset}
        />
      )}
    </div>
  );
};
